import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import HelpIcon from '@mui/icons-material/Help';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BugReportIcon from '@mui/icons-material/BugReport';
import StarIcon from '@mui/icons-material/Star';
import { casesApi } from 'src/api/casesApi';

const useStyles = makeStyles((theme) => ({
    container: {
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(1),
        backgroundColor: 'transparent',
    },
    title: {
        marginBottom: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.primary,
        fontSize: '0.9rem',
    },
    description: {
        marginBottom: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
    },
    gridContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.spacing(1),
        width: '100%',
    },
    categoryCard: {
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4],
        },
    },
    categoryContent: {
        textAlign: 'center',
        padding: theme.spacing(1),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    categoryIcon: {
        fontSize: '20px',
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(0.5),
    },
    categoryTitle: {
        fontWeight: 'bold',
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(0.25),
        fontSize: '0.8rem',
    },
    categorySubtitle: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        textAlign: 'center',
    },
}));

export interface Category {
    id: string;
    name: string;
    fullName: string;
    description: string;
    icon: React.ComponentType<any>;
}

interface CategorySelectorProps {
    onCategorySelect: (category: Category) => void;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
    onCategorySelect,
}) => {
    const classes = useStyles();
    const [caseTypes, setCaseTypes] = useState<any[]>([]);
    useEffect(() => {
        const fetchCaseTypes = async () => {
            const caseTypesResponse = await casesApi.getCaseTypes();
            setCaseTypes(caseTypesResponse.data);
        };
        fetchCaseTypes();
    }, []);
    const iconMap = {
        'General Support': HelpIcon,
        'Application Support': SupportAgentIcon,
        'Application Bug': BugReportIcon,
        'Application Feature Request': StarIcon,
    };
    const nameMap = {
        'General Support': 'General',
        'Application Support': 'Support',
        'Application Bug': 'Bug',
        'Application Feature Request': 'Feature',
    };
    const descriptionMap = {
        'General Support': 'General questions and inquiries',
        'Application Support': 'Get help with existing features',
        'Application Bug': 'Report issues or problems',
        'Application Feature Request': 'Request new features or improvements',
    };

    const caseTypesMap = caseTypes
        .map((caseType) => ({
            id: caseType.id,
            name: nameMap[caseType.name] || caseType.name,
            fullName: caseType.name,
            description: descriptionMap[caseType.name] || caseType.description,
            icon: iconMap[caseType.name] || HelpIcon,
        }));

    const categories: Category[] = caseTypesMap;

    const handleCategoryClick = (category: Category) => {
        onCategorySelect(category);
    };

    return (
        <Box className={classes.container}>
            <Typography variant="h6" className={classes.title}>
                What can we help you with today?
            </Typography>

            <Typography className={classes.description}>
                Choose the category that best fits your request
            </Typography>

            <Box className={classes.gridContainer}>
                {categories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                        <Box key={category.id} className={classes.categoryCard}>
                            <Button
                                variant="outlined"
                                sx={{
                                    minWidth: '100%',
                                    padding: '8px 16px',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onClick={() => handleCategoryClick(category)}
                            >
                                <IconComponent className={classes.categoryIcon} />
                                <Typography className={classes.categoryTitle}>
                                    {category.name}
                                </Typography>
                            </Button>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default CategorySelector; 